import React, { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useSetAtom } from 'jotai';
import { chargingAtom, chargerTimeoutAtom } from '../atoms/chargers';
import useWebSocket from '../hooks/wsHook';
import ChargingGraph from '../charts/ChargingGraph';
import { Charger, BuildingIDProps } from '../atoms/chargers';
import { fpmEndpointLink, fpmWebsocketLink } from '../../../index';
import { Card, CardContent } from '@mui/material';

const TIMEOUT_MS = 10000;

/**
 * Read-only charging snapshot handler. Fetches the latest per-building
 * charger state from FPM and subscribes to the `charger` WebSocket channel
 * for live updates. No NodeID filter (GolfTrack users don't slice by node).
 */
const ChargingGraphHandler: React.FC<BuildingIDProps> = ({ BuildingID }) => {
  const { messages } = useWebSocket(fpmWebsocketLink, ['charger']);
  const setChargingData = useSetAtom(chargingAtom);
  const setChargerTimeout = useSetAtom(chargerTimeoutAtom);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const buildingKey = BuildingID ? BuildingID.toLowerCase() : '';

  useQuery({
    queryKey: ['fpmChargerGraph', BuildingID],
    staleTime: Infinity,
    enabled: !!BuildingID,
    queryFn: async () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (buildingKey) {
        timeoutRef.current = setTimeout(() => {
          setChargerTimeout((prev) => ({ ...prev, [buildingKey]: true }));
        }, TIMEOUT_MS);
      }

      const response = await axios.get<Charger[]>(`${fpmEndpointLink}charger-graph`, {
        params: { BuildingID },
      });
      const data = response.data;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (buildingKey) {
        setChargerTimeout((prev) => {
          const updated = { ...prev };
          delete updated[buildingKey];
          return updated;
        });
      }

      updateChargingData(data);
      return data;
    },
  });

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [BuildingID]);

  function updateChargingData(data: Charger[]) {
    if (!data || data.length === 0) return;

    const parsedData = data.reduce((acc, chargingData) => {
      if (!chargingData.BuildingID) return acc;
      const key = chargingData.BuildingID.toLowerCase();
      acc[key] = {
        BuildingID: chargingData.BuildingID,
        NChans: chargingData.NChans,
        NCharging: chargingData.NCharging,
        NWaiting: chargingData.NWaiting,
        Timestamp: chargingData.Timestamp,
        CState: typeof chargingData.CState === 'string'
          ? JSON.parse(chargingData.CState)
          : chargingData.CState,
        CPower: typeof chargingData.CPower === 'string'
          ? JSON.parse(chargingData.CPower)
          : chargingData.CPower,
        CEnergy: typeof chargingData.CEnergy === 'string'
          ? JSON.parse(chargingData.CEnergy)
          : chargingData.CEnergy,
      };
      return acc;
    }, {} as Record<string, Charger>);

    setChargingData((prev) => ({ ...prev, ...parsedData }));
  }

  useEffect(() => {
    messages.forEach((messageObj) => {
      const { channel, message } = messageObj;
      if (channel !== 'charger') return;
      try {
        if (message && typeof message === 'object' && 'BuildingID' in message) {
          const b = String(message.BuildingID).toLowerCase();
          const parsedMessage = { ...message } as Charger;

          parsedMessage.CState = typeof parsedMessage.CState === 'string'
            ? JSON.parse(parsedMessage.CState)
            : parsedMessage.CState || [];
          parsedMessage.CPower = typeof parsedMessage.CPower === 'string'
            ? JSON.parse(parsedMessage.CPower)
            : parsedMessage.CPower || [];
          parsedMessage.CEnergy = typeof parsedMessage.CEnergy === 'string'
            ? JSON.parse(parsedMessage.CEnergy)
            : parsedMessage.CEnergy || [];

          setChargingData((prev) => ({ ...prev, [b]: parsedMessage }));

          if (b) {
            setChargerTimeout((prev) => {
              const updated = { ...prev };
              delete updated[b];
              return updated;
            });
          }
        }
      } catch (error) {
        console.error('Failed to parse charger WebSocket message:', error);
      }
    });
  }, [messages, setChargingData, setChargerTimeout]);

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
      <CardContent
        sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          minWidth: 0,
          width: '100%',
          padding: '16px',
        }}
      >
        <ChargingGraph BuildingID={BuildingID} />
      </CardContent>
    </Card>
  );
};

export default ChargingGraphHandler;
